<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useUserStore } from "@/stores/userStore";
import { useEntitlementStore } from "@/stores/entitlementStore";
import { useCompanyFeatureStore } from "@/stores/companyFeatureStore";

const STORAGE_LOCALE = "mkzugferd.locale";
const userStore = useUserStore();
const entitlementStore = useEntitlementStore();
const companyFeatureStore = useCompanyFeatureStore();
const handbookFrame = ref(null);

const activeLanguage = computed(() => {
  const locale = (userStore.locale || localStorage.getItem(STORAGE_LOCALE) || "de").toLowerCase();
  return locale.startsWith("de") ? "de" : "en";
});

const handbookTitle = computed(() =>
  activeLanguage.value === "de" ? "Handbuch" : "Handbook"
);

const handbookUrl = computed(() => `/handbook/${activeLanguage.value}/index.html`);

const access = computed(() => {
  const permission = (code) => userStore.hasPermission(code);
  const module = (id) => entitlementStore.hasModule(id);
  const feature = (id) => companyFeatureStore.companyId === userStore.currentCompanyId
    && companyFeatureStore.isEnabled(id);
  const invoices = module("invoicing") && module("e-invoice") && permission("zugferd.read");
  const invoiceCreate = invoices && permission("invoices.write");
  const invoiceExport = invoices && permission("invoices.export");
  const invoiceImport = invoices && permission("invoices.import");
  const invoiceValidate = invoices && permission("zugferd.validate");
  const workforce = feature("workforce");
  const workforceTime = workforce && module("workforce-time");
  const workforceLeaveModule = workforce && module("leave");
  const workforceMonth = workforceTime && (permission("workforce.self.read") || permission("workforce.team.read"));
  const workforceEntry = workforceTime && permission("workforce.time.write");
  const workforceLeave = workforceLeaveModule && permission("workforce.self.read");
  const workforceLeaveRequest = workforceLeave && permission("workforce.leave.request");
  const workforceApprovals = workforceLeaveModule && permission("workforce.leave.approve");
  const workforceHistory = workforceTime && (permission("workforce.self.read") || permission("workforce.team.read"));
  const workforceImport = workforceTime && permission("workforce.import");
  const bankRead = feature("banking-opos") && module("banking") && permission("banking.read");
  const oposRead = feature("banking-opos") && module("opos") && permission("opos.read");
  const bankMatch = bankRead && oposRead;
  const bankManage = bankMatch && permission("opos.manage");
  const billingRead = feature("billing") && module("invoicing") && module("kimai-integration")
    && permission("billing.read");
  const invoicingAdmin = module("invoicing");
  const adminCompanies = invoicingAdmin && permission("companies.read");
  const adminCustomers = invoicingAdmin && permission("customers.read");
  const adminArticles = invoicingAdmin && permission("articles.read");
  const adminTemplates = invoicingAdmin && permission("files.read");
  const adminUsers = permission("admin.users.read");
  const adminGroups = permission("admin.groups.read");
  const adminRoles = permission("admin.roles.read");
  const adminIdentityProviders = permission("admin.identityproviders.read");
  const adminProvisioning = permission("provisioning.requests.read");

  return {
    invoices,
    invoiceCreate,
    invoiceExport,
    invoiceImport,
    invoiceValidate,
    invoiceFinalize: invoiceCreate && invoiceExport && invoiceImport && invoiceValidate,
    workforce: [workforceMonth, workforceEntry, workforceLeave, workforceApprovals, workforceHistory, workforceImport]
      .some(Boolean),
    workforceMonth,
    workforceEntry,
    workforceLeave,
    workforceLeaveRequest,
    workforceApprovals,
    workforceHistory,
    workforceImport,
    bankingOpos: bankRead || oposRead,
    bankRead,
    oposRead,
    bankMatch,
    bankManage,
    billingRead,
    billingManage: billingRead && permission("billing.manage"),
    billingApprove: billingRead && permission("billing.approve"),
    billingDispatch: billingRead && permission("billing.dispatch"),
    optional: [workforceMonth, workforceEntry, workforceLeave, workforceApprovals, workforceHistory,
      workforceImport, bankRead, oposRead, billingRead].some(Boolean),
    administration: [adminCompanies, adminCustomers, adminArticles, adminTemplates, adminUsers, adminGroups,
      adminRoles, adminIdentityProviders, adminProvisioning].some(Boolean),
    adminCompanies,
    adminCustomers,
    adminArticles,
    adminTemplates,
    adminUsers,
    adminGroups,
    adminRoles,
    adminIdentityProviders,
    adminProvisioning,
    adminFeatures: userStore.isSystemAdmin && adminCompanies,
  };
});

function updateHandbookVisibility() {
  const document = handbookFrame.value?.contentDocument;
  if (!document || !/^\/handbook\/(de|en)\/index\.html$/.test(document.location.pathname)) return;
  for (const element of document.querySelectorAll("[data-access]")) {
    element.hidden = access.value[element.dataset.access] !== true;
  }
}

watch(access, updateHandbookVisibility);

onMounted(async () => {
  try {
    if (!userStore.me) await userStore.loadMe();
    if (!entitlementStore.isLoaded) await entitlementStore.loadCapabilities();
  } catch {
    // Restricted sections remain hidden when the access data cannot be loaded.
  }
});

function openInNewTab() {
  window.open("/help/handbook", "_blank", "noopener,noreferrer");
}
</script>

<template>
  <div class="container-fluid py-3 px-3 px-lg-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3 class="m-0">{{ handbookTitle }}</h3>
      <button type="button" class="btn btn-outline-secondary btn-sm" @click="openInNewTab">
        {{ activeLanguage === "de" ? "In neuem Tab öffnen" : "Open in new tab" }}
      </button>
    </div>

    <iframe
      ref="handbookFrame"
      :src="handbookUrl"
      @load="updateHandbookVisibility"
      title="E-Invoicing Handbook"
      style="width: 100%; min-height: calc(100vh - 180px); border: 1px solid #d1d5db; border-radius: 10px; background: #fff"
    />
  </div>
</template>
